import {
  Badge,
  Box,
  Flex,
  FormControl,
  Grid,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react';
import { WarningComponent } from '@texas/components/shared/WarningComponent';
import { TexasFormLabel } from '@texas/components/shared/form/TexasFormLabel';
import { TexasSelect } from '@texas/components/shared/form/TexasSelect';
import {
  articleWorkspaceStyles,
  copyArticleSelectStyles,
  copyArticleStyles,
} from '@texas/resources/theme/styles/entityWorkspace';
import { ReactSelectOption } from '@texas/types';
import { useTranslation } from 'react-i18next';
import { CopyArticleSectionHeading } from './CopyArticleSectionHeading';
import { useCopyArticle } from './useCopyArticle';

export function CopyArticleSource() {
  const { t } = useTranslation();
  const { loading, source } = useCopyArticle();

  return (
    <Box sx={articleWorkspaceStyles.card}>
      <CopyArticleSectionHeading
        number="1"
        title={t('article.copy.sourceTitle')}
        description={t('article.copy.sourceDescription')}
      />
      <Stack spacing={4} sx={copyArticleStyles.cardBody}>
        <SourceSelectors />
        {loading && !source && (
          <Flex sx={copyArticleStyles.sourceLoading}>
            <Spinner size="sm" />
            <Text sx={copyArticleStyles.sourceLoadingText}>
              {t('article.copy.loadingSource')}
            </Text>
          </Flex>
        )}
        {source && (
          <Flex sx={copyArticleStyles.sourceSelected}>
            <Box>
              <Text sx={copyArticleStyles.sourceName}>
                {source.itemCode} · {source.name}
              </Text>
              <Text sx={copyArticleStyles.sourceDetails}>
                {source.customerName}
                {source.conceptName ? ` / ${source.conceptName}` : ''} ·{' '}
                {source.variants.length} {t('article.copy.variants')}
              </Text>
            </Box>
            <Badge sx={copyArticleStyles.sourceBadge}>
              {t('article.copy.sourceSelected')}
            </Badge>
          </Flex>
        )}
      </Stack>
    </Box>
  );
}

function SourceSelectors() {
  const { t } = useTranslation();
  const {
    setValue,
    fieldWarnings,
    loadSourceArticle,
    loadSourceCustomer,
    sourceArticleOption,
    sourceArticleOptions,
    sourceCustomerId,
    sourceCustomerOption,
    sourceCustomerSearch,
  } = useCopyArticle();

  return (
    <Grid sx={copyArticleStyles.sourceSelectors}>
      <FormControl
        sx={articleWorkspaceStyles.numberField}
        id={fieldWarnings.sourceCustomer.targetId}
        isInvalid={!!fieldWarnings.sourceCustomer.warning}
        isRequired={true}
      >
        <WarningComponent {...fieldWarnings.sourceCustomer}>
          <TexasFormLabel>{t('article.copy.customer')}</TexasFormLabel>
        </WarningComponent>
        <TexasSelect<ReactSelectOption>
          {...copyArticleSelectStyles}
          inputValue={sourceCustomerSearch.input}
          isLoading={sourceCustomerSearch.loading}
          noOptionsMessage={() =>
            sourceCustomerSearch.input.trim().length < 2
              ? t('article.copy.customerSearchMinimum')
              : t('general.noResult')
          }
          onChange={(option) => {
            if (!option) return;
            setValue('sourceCustomerOption', option);
            loadSourceCustomer(Number(option.value));
          }}
          onInputChange={sourceCustomerSearch.setInput}
          options={sourceCustomerSearch.options}
          placeholder={t('article.copy.chooseSourceCustomer')}
          value={sourceCustomerOption}
        />
      </FormControl>
      <FormControl
        sx={articleWorkspaceStyles.numberField}
        id={fieldWarnings.sourceArticle.targetId}
        isInvalid={!!fieldWarnings.sourceArticle.warning}
        isRequired={true}
      >
        <WarningComponent {...fieldWarnings.sourceArticle}>
          <TexasFormLabel>{t('article.copy.sourceArticle')}</TexasFormLabel>
        </WarningComponent>
        <TexasSelect<ReactSelectOption>
          {...copyArticleSelectStyles}
          isDisabled={!sourceCustomerId}
          noOptionsMessage={() => t('general.noResult')}
          onChange={(option) => {
            if (option) loadSourceArticle(option);
          }}
          options={sourceArticleOptions}
          placeholder={t('article.copy.chooseSourceArticle')}
          value={sourceArticleOption}
        />
      </FormControl>
    </Grid>
  );
}
